import * as React from 'react';
import ConfirmationModal from './ConfirmationModal';
import FormSnackbar from './FormSnackbar';

/**
 * Snackbar state interface
 */
export interface ISnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning';
}

/**
 * Props for the ValidationModals component
 */
export interface IValidationModalsProps {
  /** Whether the confirmation modal is open */
  showModal: boolean;
  /** Modal message to display */
  modalMessage: string;
  /** Modal title to display */
  modalTitle: string;
  /** Whether the modal allows continuation */
  canContinue?: boolean;
  /** Function to handle modal confirmation */
  onConfirm: () => void;
  /** Function to handle modal cancellation */
  onCancel: () => void;
  /** Snackbar state for success/error messages */
  snackbar: ISnackbarState;
  /** Function to close the snackbar */
  onSnackbarClose: () => void;
}

/**
 * ValidationModals component that handles confirmation modals and snackbars
 * Provides a centralized location for all modal and notification UI
 *
 * @param props - Component props
 * @returns JSX element containing modals and snackbars
 */
export const ValidationModals: React.FC<IValidationModalsProps> = React.memo(
  ({
    showModal,
    modalMessage,
    modalTitle,
    canContinue,
    onConfirm,
    onCancel,
    snackbar,
    onSnackbarClose,
  }) => {
    return (
      <>
        <ConfirmationModal
          open={showModal}
          message={modalMessage}
          title={modalTitle || 'Confirm'}
          onConfirm={onConfirm}
          onCancel={onCancel}
          canContinue={canContinue}
        />
        <FormSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={onSnackbarClose}
        />
      </>
    );
  }
);

ValidationModals.displayName = 'ValidationModals';
